import { platformClient, PlatformResponse } from '../client/platform-client';
import { ENDPOINTS } from '../api/endpoints';
import { PlatformProjectData } from '../types/platform-types';

export class ProjectsService {
  static async list(params?: any): Promise<PlatformResponse<any[]>> {
    return platformClient.get(ENDPOINTS.projects.list, { params });
  }

  static async getDetails(id: string | number): Promise<PlatformResponse<any>> {
    return platformClient.get(ENDPOINTS.projects.byId(String(id)));
  }

  static async getTasks(projectId: string | number, params?: any): Promise<PlatformResponse<any>> {
    return platformClient.get(ENDPOINTS.projects.tasks(String(projectId)), { params });
  }

  static async getSprints(projectId: string | number, params?: any): Promise<PlatformResponse<any>> {
    return platformClient.get(ENDPOINTS.projects.sprints(String(projectId)), { params });
  }

  static async getMilestones(projectId: string | number, params?: any): Promise<PlatformResponse<any>> {
    return platformClient.get(ENDPOINTS.projects.milestones(String(projectId)), { params });
  }

  static async getMembers(projectId: string | number, params?: any): Promise<PlatformResponse<any>> {
    return platformClient.get(ENDPOINTS.projects.members(String(projectId)), { params });
  }

  static async getComments(projectId: string | number, params?: any): Promise<PlatformResponse<any>> {
    return platformClient.get(`/api/projects/${String(projectId)}/comments`, { params });
  }

  static async createMember(projectId: string | number, data: any): Promise<any> {
    const resp = await platformClient.post(`/api/projects/${projectId}/members`, data);
    return (resp as any).data || resp;
  }

  static async updateMember(projectId: string | number, memberId: string, data: any): Promise<any> {
    const resp = await platformClient.put(`/api/projects/${projectId}/members/${memberId}`, data);
    return (resp as any).data || resp;
  }

  static async deleteMember(projectId: string | number, memberId: string): Promise<any> {
    const resp = await platformClient.delete(`/api/projects/${projectId}/members/${memberId}`);
    return (resp as any).data || resp;
  }

  static async createTask(projectId: string | number, data: any): Promise<any> {
    const resp = await platformClient.post(`/api/projects/${projectId}/tasks`, data);
    return (resp as any).data || resp;
  }

  static async deleteTask(taskId: string | number): Promise<any> {
    const resp = await platformClient.delete(`/api/tasks/${taskId}`);
    return (resp as any).data || resp;
  }

  static async updateTaskStatus(taskId: string | number, status: string): Promise<any> {
    const resp = await platformClient.put(`/api/tasks/${taskId}`, { status });
    return (resp as any).data || resp;
  }

  static async createComment(projectId: string | number, data: any): Promise<any> {
    const resp = await platformClient.post(`/api/projects/${projectId}/comments`, data);
    return (resp as any).data || resp;
  }

  static async deleteComment(projectId: string | number, commentId: string): Promise<any> {
    const resp = await platformClient.delete(`/api/projects/${projectId}/comments/${commentId}`);
    return (resp as any).data || resp;
  }

  static async createSprint(projectId: string | number, data: any): Promise<any> {
    const resp = await platformClient.post(`/api/projects/${projectId}/sprints`, data);
    return (resp as any).data || resp;
  }

  static async updateSprint(projectId: string | number, sprintId: string, data: any): Promise<any> {
    const resp = await platformClient.put(`/api/projects/${projectId}/sprints/${sprintId}`, data);
    return (resp as any).data || resp;
  }

  static async deleteSprint(projectId: string | number, sprintId: string): Promise<any> {
    const resp = await platformClient.delete(`/api/projects/${projectId}/sprints/${sprintId}`);
    return (resp as any).data || resp;
  }

  static async createMilestone(projectId: string | number, data: any): Promise<any> {
    const resp = await platformClient.post(`/api/projects/${projectId}/milestones`, data);
    return (resp as any).data || resp;
  }

  static async updateMilestone(projectId: string | number, milestoneId: string, data: any): Promise<any> {
    const resp = await platformClient.put(`/api/projects/${projectId}/milestones/${milestoneId}`, data);
    return (resp as any).data || resp;
  }

  static async deleteMilestone(projectId: string | number, milestoneId: string): Promise<any> {
    const resp = await platformClient.delete(`/api/projects/${projectId}/milestones/${milestoneId}`);
    return (resp as any).data || resp;
  }

  static async getAuditLogs(projectId: string | number, params?: any): Promise<PlatformResponse<any>> {
    return platformClient.get(`/api/projects/${projectId}/audit-logs`, { params });
  }

  // Compatibility wrappers to match older ProjectsService API used across the app
  static async getById(id: string | number): Promise<any> {
    const resp = await this.getDetails(String(id));
    return (resp as any).data || resp;
  }

  static async create(data: any): Promise<any> {
    const resp = await platformClient.post(ENDPOINTS.projects.list, data);
    return (resp as any).data || resp;
  }

  static async update(id: string | number, data: any): Promise<any> {
    const resp = await platformClient.put(ENDPOINTS.projects.byId(String(id)), data);
    return (resp as any).data || resp;
  }

  static async delete(id: string | number): Promise<any> {
    const resp = await platformClient.delete(ENDPOINTS.projects.byId(String(id)));
    return (resp as any).data || resp;
  }

  static async getKanbanBoard(id: string | number): Promise<any> {
    try {
      const resp = await platformClient.get(ENDPOINTS.projects.kanban(String(id)));
      return (resp as any).data || resp;
    } catch (err) {
      const tasksResp = await this.getTasks(String(id));
      const tasks = (tasksResp as any).data || tasksResp;
      return {
        todo: tasks.filter((t: any) => t.status === 'TODO'),
        inProgress: tasks.filter((t: any) => t.status === 'IN_PROGRESS'),
        review: tasks.filter((t: any) => t.status === 'REVIEW'),
        done: tasks.filter((t: any) => t.status === 'DONE'),
      };
    }
  }

  static async getProjectDocuments(projectId: string | number): Promise<any> {
    const resp = await platformClient.get(`/api/documents/resource/project/${projectId}`);
    return (resp as any).data || resp;
  }

  static async getProjectFinance(projectId: string | number): Promise<any> {
    const resp = await platformClient.get(`/api/finance`, { params: { projectId: String(projectId) } });
    return (resp as any).data || resp;
  }

  static async getReactions(entityType: string, entityId: string | number): Promise<any> {
    const resp = await platformClient.get(`/api/reactions`, { params: { entityType, entityId: String(entityId) } });
    return (resp as any).data || resp;
  }

  static async createReaction(entityType: string, entityId: string | number, type: string): Promise<any> {
    const resp = await platformClient.post(`/api/reactions`, { entityType, entityId: String(entityId), type });
    return (resp as any).data || resp;
  }

  static async deleteReaction(entityType: string, entityId: string | number, type: string): Promise<any> {
    const resp = await platformClient.delete(`/api/reactions`, { params: { entityType, entityId: String(entityId), type } });
    return (resp as any).data || resp;
  }

  static async getDashboard(id: string | number): Promise<any> {
    try {
      const v2Url = `/api/v2/projects/${id}/dashboard`;
      const v2Resp = await platformClient.get(v2Url);
      if (v2Resp && (v2Resp as any).data) {
        const body = (v2Resp as any).data;
        if (body && body.success && body.data) return body.data;
        if (body && body.data) return body.data;
      }
    } catch (err) {
      // ignore and fallback
    }

    const resp = await platformClient.get(ENDPOINTS.projects.dashboard(String(id)));
    return (resp as any).data || resp;
  }
}